import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Paper,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Languages,
  TextModes,
  DarkModes,
  TextSizes,
} from "entities/constants";
import RadioButtons from "ui/components/shared/RadioButtons";
import ImageComponent from "ui/components/shared/ImageComponent";
import {
  TextSizeContext,
  DarkModeContext,
  BackgroundContext,
} from "data/index";

import Background1 from "assets/images/Background.png";
import Background2 from "assets/images/Background_Chat.png";

// === Styled Components ===
const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: "2rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1.5rem",
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: "2rem",
  borderRadius: "16px",
  boxShadow: theme.shadows[1],
  display: "flex",
  flexDirection: "column",
  gap: "1.8rem",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const BackgroundGrid = styled(Box)(() => ({
  display: "flex",
  gap: "1rem",
  flexWrap: "wrap",
  opacity: 0.5,
  pointerEvents: "none",
}));

const BackgroundTile = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  width: "5rem",
  height: "5rem",
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: selected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
  boxShadow: theme.shadows[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// === Component ===
const LearningPreferenceTab = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { state: textSize, updateContext: setTextSize } = useContext(TextSizeContext);
  const { state: darkMode } = useContext(DarkModeContext);
  const { state: backgroundImage } = useContext(BackgroundContext);

  const [textMode, setTextMode] = useState<number>(1);
  const [language, setLanguage] = useState(i18n.language === "de" ? 0 : 1);

  const handleTextModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextMode(Number(e.target.value));
  };

  const handleTextSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) as 0 | 1 | 2;
    setTextSize(value);
    localStorage.setItem("textSize", value.toString());
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const langCode = value === "0" ? "de" : "en";
    setLanguage(Number(value));
    i18n.changeLanguage(langCode);
    localStorage.setItem("language", langCode);
  };

  return (
    <Wrapper>
      {/* Sprache - separates Card */}
      <Card>
        <SectionTitle>{t("Language")}</SectionTitle>
        <RadioButtons
          options={Languages.map((lang) => t(lang))}
          value={language}
          onChange={handleLanguageChange}
        />
      </Card>

      {/* Alle anderen Einstellungen in EINEM Card */}
      <Card>
        <Box>
          <SectionTitle>{t("Text Mode")}</SectionTitle>
          <RadioButtons
            options={TextModes.map((m) => t(m))}
            value={textMode}
            onChange={handleTextModeChange}
          />
        </Box>

        <Divider />

        <Box>
          <SectionTitle>{t("Text Size")}</SectionTitle>
          <RadioButtons
            options={[t("Small"), t("Medium"), t("Large")]}
            value={textSize}
            onChange={handleTextSizeChange}
          />
        </Box>

        <Divider />

        <Box sx={{ opacity: 0.5, pointerEvents: "none" }}>
          <SectionTitle>{t("Dark Mode")}</SectionTitle>
          <RadioButtons
            options={DarkModes.map((mode) => t(mode))}
            value={darkMode ? 0 : 1}
          />
        </Box>

        <Divider />

        <Box sx={{ opacity: 0.5, pointerEvents: "none" }}>
          <SectionTitle>{t("Background")}</SectionTitle>
          <BackgroundGrid>
            {[Background1, Background2].map((img, index) => (
              <BackgroundTile key={index} selected={backgroundImage === index + 1}>
                <ImageComponent
                  src={img}
                  alt={`background-${index + 1}`}
                  width="100%"
                  height="100%"
                  transform="scale(3)"
                />
              </BackgroundTile>
            ))}
          </BackgroundGrid>
        </Box>
      </Card>
    </Wrapper>
  );
};

export default LearningPreferenceTab;
